/**
 * **The bench: the page measuring itself** (B0.6).
 *
 * B0.1 left the platform matrix half filled and named the reason — half the
 * cells are browsers and webviews nobody can script. Chrome has a debug
 * protocol, Firefox removed its; Safari needs a Mac and a person; WebView2,
 * WKWebView and WebKitGTK have no automation surface at all worth the name. A
 * driver would therefore fill exactly the cells that were already easy.
 *
 * So the instrument is *inside the build*. Open the page with `?bench=1` on any
 * browser, webview or wrapper, wait, and the run posts its own numbers back to
 * whatever served it — and prints them to the console, and parks them on
 * `window.yieldBench`, so a cell can also be filled by hand from a device with
 * no terminal on it. `web/scripts/bench.mjs` is only the other end of the wire.
 *
 * ## What it measures, and why in this order
 *
 * A **ladder** of loads, each held long enough to be a measurement rather than a
 * reading, matching the rungs `harness::bench` walks natively so the two tables
 * can be read side by side. At each rung the run records the frame split, the
 * boundary's bytes and rate, the worker's own cost, and the verdict against the
 * budget the *sim* stated (never one written here).
 *
 * Two things about the method are load-bearing.
 *
 * **Every stage settles before it samples.** The crew dial is a hiring decision
 * inside the sim: bodies appear at the door and walk to work. Sampling
 * immediately would measure a crowd in transit, which is both the cheapest and
 * the least representative moment in a stage's life.
 *
 * **The stress is applied through the command queue**, exactly as a click would
 * be — no privileged path, no test hook into the sim. If a stage cannot be
 * reached by sending commands, it is not a state the shipped build can be in,
 * and measuring it would be measuring a fiction.
 */

import type { FloorCommand, FloorHost } from './bridge';
import { environmentReport, type EnvironmentReport } from './boot';
import { flush, snapshot, type HudState } from './ui/store';

/** One rung of the ladder: what to send, and what it is called in the table. */
interface Stage {
  name: string;
  commands: FloorCommand[];
}

/**
 * The ladder. It mirrors `harness::bench::LADDER` deliberately — same names,
 * same shape — because the whole value of having a native table and a browser
 * table is being able to put them beside each other.
 *
 * The dense fab is reached by *importing a layout*, which is a command like any
 * other, so the browser run stresses the same floor the native one does without
 * either side carrying a copy of it: the JSON comes from the sim.
 */
function ladder(dense: string | null): Stage[] {
  const stages: Stage[] = [
    { name: 'play scale', commands: [{ kind: 'setCrew', count: 30 }, { kind: 'setStarts', lotsPerDay: 4 }] },
  ];
  if (dense) {
    stages.push(
      {
        name: 'a real fab',
        commands: [
          { kind: 'loadLayout', json: dense },
          { kind: 'setCrew', count: 30 },
          { kind: 'setStarts', lotsPerDay: 24 },
        ],
      },
      { name: 'the design envelope', commands: [{ kind: 'setCrew', count: 512 }] },
      { name: 'past the envelope', commands: [{ kind: 'setCrew', count: 2048 }] },
      { name: "the ring's ceiling", commands: [{ kind: 'setCrew', count: 4096 }] },
    );
  }
  return stages;
}

/** What one rung produced. Everything here is read out of the live HUD store. */
export interface BenchRow {
  stage: string;
  transport: string | null;
  isolated: boolean;
  lod: boolean;
  /** Frame. */
  fps: number;
  frameMs: number;
  frameMsP95: number;
  scriptMs: number;
  scriptMsP95: number;
  renderMs: number;
  boundaryMs: number;
  longTasksPerSecond: number;
  longestTaskMs: number;
  longTasksSupported: boolean;
  /** What was on screen. */
  agents: number;
  structures: number;
  lots: number;
  instances: number;
  detailRadius: number;
  cullRadius: number;
  /** Boundary. */
  publishHz: number;
  bytesPerPublish: number;
  bytesPerSecond: number;
  heatBytes: number;
  commandsPerSecond: number;
  commandBytesPerSecond: number;
  droppedCommands: number;
  /** Worker. */
  workerStepMs: number;
  workerPublishMs: number;
  droppedSteps: number;
  /**
   * Rows the React overlays actually rendered.
   *
   * A frame number says the renderer is alive; it says nothing about the DOM
   * overlays, which are a separate tree that can throw without stopping a single
   * frame. A cell whose panels are blank is not a passing cell, and this is the
   * cheapest possible way for the bench to notice — one `querySelectorAll`, once
   * per rung, off the frame path.
   */
  overlayRows: number;
  /** Verdict, against the budget the sim stated. */
  verdict: string[];
}

export interface BenchReport {
  startedAt: string;
  /** Seconds each rung was held, and how many of those were sampled. */
  settleSeconds: number;
  sampleSeconds: number;
  report: EnvironmentReport;
  budget: HudState['budget'];
  rows: BenchRow[];
  /** Rungs that broke the budget, by name and line. `[]` is the pass. */
  failures: string[];
}

declare global {
  interface Window {
    /** The finished run, for a device with no terminal attached. */
    yieldBench?: BenchReport;
  }
}

/**
 * **A cell that does not boot is a filled cell.** When the diagnostic stops the
 * page, this posts the environment report with no rows and the reason as the
 * failure — so a webview with no WebGPU fills its row in the matrix by being
 * opened, exactly as a working one does.
 *
 * Without this the interesting cells would be the silent ones, which is the
 * wrong way round: "does not boot, and here is why in words" is the answer with
 * a decision attached (docs/13-architecture.md — the fallback is a bundled
 * runtime, never WebGL2).
 */
export async function reportBootFailure(environment: EnvironmentReport): Promise<void> {
  const result: BenchReport = {
    startedAt: new Date().toISOString(),
    settleSeconds: 0,
    sampleSeconds: 0,
    report: environment,
    budget: null,
    rows: [],

    failures: [
      environment.webgpu.ok
        ? 'the renderer failed to start'
        : `did not boot: ${environment.webgpu.reason}`,
    ],
  };
  window.yieldBench = result;
  console.log('yield bench', JSON.stringify(result));
  await post(result);
}

/** Seconds a stage is given to settle before anything is believed. */
const SETTLE_S = 4;
/** Seconds of it that are sampled. */
const SAMPLE_S = 4;

/**
 * Run the ladder and hand back the report. Also posts it to `/bench` — the
 * collector in `scripts/bench.mjs` — and logs it, because a webview's console is
 * sometimes the only way anything gets out.
 */
export async function runBench(host: FloorHost, params: URLSearchParams): Promise<BenchReport> {
  const settle = Number(params.get('settle') ?? SETTLE_S);
  const sample = Number(params.get('sample') ?? SAMPLE_S);
  const lod = params.get('lod') !== 'off';
  const environment = await environmentReport();

  // The dense fab comes from the sim, not from a copy in here.
  const dense = await host.builtinLayout('dense-1974');

  const rows: BenchRow[] = [];
  // Speed is left at 1×: the bench measures a frame, and a frame at 8× is the
  // same frame with more theater time behind it. Raising it would only make the
  // worker busier, which is a different question and one `floor bench` answers
  // more precisely than a browser can.
  host.send({ kind: 'setSpeed', mille: 1000 });
  // The bench measures the *watched* clock (M1.1): a campaign fast-forwarding
  // has no frame path to measure.
  host.setPace('watch');

  for (const stage of ladder(dense)) {
    host.send(...stage.commands);
    await hold(settle);
    rows.push(await measure(stage.name, sample, lod));
  }

  const state = snapshot();
  const failures = rows.flatMap((row) => row.verdict.map((line) => `${row.stage}: ${line}`));
  const result: BenchReport = {
    // Wall-clock, in a *measurement*, which is the one place it belongs — the
    // determinism law is about sim state paths (docs/13-architecture.md).
    startedAt: new Date().toISOString(),
    settleSeconds: settle,
    sampleSeconds: sample,
    report: environment,
    budget: state.budget,
    rows,
    failures,
  };
  window.yieldBench = result;
  console.log('yield bench', JSON.stringify(result));
  await post(result);
  return result;
}

/**
 * Sample the HUD store over a window and average it.
 *
 * Reading the store rather than re-timing anything is the point: these are the
 * *same* numbers the panel shows and the same ones a person would copy off the
 * screen with the report button, so an automated cell and a hand-filled one are
 * the same measurement.
 */
async function measure(stage: string, seconds: number, lod: boolean): Promise<BenchRow> {
  const samples: HudState[] = [];
  const until = performance.now() + seconds * 1000;
  while (performance.now() < until) {
    await hold(0.2);
    samples.push(snapshot());
  }
  const mean = (pick: (s: HudState) => number) =>
    samples.reduce((sum, s) => sum + pick(s), 0) / Math.max(samples.length, 1);
  const worst = (pick: (s: HudState) => number) =>
    samples.reduce((most, s) => Math.max(most, pick(s)), 0);
  const last = samples[samples.length - 1] ?? snapshot();

  const row: BenchRow = {
    stage,
    transport: last.transport,
    isolated: last.isolated,
    lod,
    fps: mean((s) => s.fps),
    frameMs: mean((s) => s.frameMs),
    frameMsP95: worst((s) => s.frameMsP95),
    scriptMs: mean((s) => s.scriptMs),
    scriptMsP95: worst((s) => s.scriptMsP95),
    renderMs: mean((s) => s.renderMs),
    boundaryMs: mean((s) => s.boundaryMs),
    longTasksPerSecond: mean((s) => s.longTasks),
    longestTaskMs: worst((s) => s.longestTaskMs),
    longTasksSupported: last.longTasksSupported,
    agents: Math.round(mean((s) => s.drawn)),
    structures: Math.round(mean((s) => s.structures)),
    lots: Math.round(mean((s) => s.lots)),
    instances: Math.round(mean((s) => s.drawn + s.structures + s.lots)),
    detailRadius: last.lod?.detailRadius ?? 0,
    cullRadius: last.lod?.cullRadius ?? 0,
    publishHz: mean((s) => s.publishHz),
    bytesPerPublish: Math.round(mean((s) => s.bytesPerPublish)),
    bytesPerSecond: Math.round(mean((s) => s.bytesPerSecond)),
    heatBytes: Math.round(mean((s) => s.heatBytes)),
    commandsPerSecond: mean((s) => s.commandsPerSecond),
    commandBytesPerSecond: mean((s) => s.commandBytesPerSecond),
    droppedCommands: last.droppedCommands,
    workerStepMs: mean((s) => s.worker?.stepMs ?? 0),
    workerPublishMs: mean((s) => s.worker?.publishMs ?? 0),
    droppedSteps: last.worker?.droppedSteps ?? 0,
    overlayRows: document.querySelectorAll('.hud .row').length,
    verdict: [],
  };
  row.verdict = judge(row, last);
  flush();
  return row;
}

/**
 * Hold a row against the budget **the sim stated**. Every threshold here is read
 * from `state.budget`; none is written in this file, which is the whole reason
 * the budget crosses the boundary at all.
 */
function judge(row: BenchRow, state: HudState): string[] {
  const budget = state.budget;
  if (!budget) return ['no budget was handed over — the boundary did not complete boot'];
  const broken: string[] = [];
  if (row.fps < budget.targetFps * 0.95) {
    broken.push(`${row.fps.toFixed(0)} fps against a target of ${budget.targetFps}`);
  }
  if (row.scriptMs * 1000 > budget.mainThreadUs) {
    broken.push(
      `main thread ${row.scriptMs.toFixed(2)} ms against ${(budget.mainThreadUs / 1000).toFixed(2)} ms`,
    );
  }
  if (row.workerStepMs * 1000 > budget.workerStepUs) {
    broken.push(
      `worker step ${row.workerStepMs.toFixed(3)} ms against ${(budget.workerStepUs / 1000).toFixed(3)} ms`,
    );
  }
  if (row.workerPublishMs * 1000 > budget.workerPublishUs) {
    broken.push(
      `worker publish ${row.workerPublishMs.toFixed(3)} ms against ${(budget.workerPublishUs / 1000).toFixed(3)} ms`,
    );
  }
  // The invariant, not a threshold: the publish rate may not move with the load.
  // Allowed a tenth of slack because it is sampled over a wall-clock window.
  if (Math.abs(row.publishHz - budget.publishHz) > budget.publishHz * 0.1) {
    broken.push(
      `${row.publishHz.toFixed(1)} publishes/s against a tick rate of ${budget.publishHz} — the boundary's cost has started growing in calls`,
    );
  }
  if (row.bytesPerSecond / 1024 > budget.boundaryKbPerSecond) {
    broken.push(
      `${(row.bytesPerSecond / 1024).toFixed(0)} kB/s against a ceiling of ${budget.boundaryKbPerSecond}`,
    );
  }
  if (row.droppedSteps > 0) broken.push(`${row.droppedSteps} steps dropped`);
  if (row.droppedCommands > 0) broken.push(`${row.droppedCommands} command batches dropped`);
  if (row.overlayRows === 0) {
    broken.push('the overlays rendered nothing — React threw, or the store never reached it');
  }
  return broken;
}

function hold(seconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, seconds * 1000));
}

/**
 * Post the report to whatever served the page. A failure is expected and
 * ignored: a cell filled by opening the file from a device with no collector
 * behind it is still a filled cell, and `window.yieldBench` is where it lives.
 */
async function post(result: BenchReport): Promise<void> {
  try {
    await fetch('/bench', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(result),
    });
  } catch {
    /* no collector listening; the console and `window.yieldBench` still have it */
  }
}
